import nanoid from 'nanoid';
import { format } from 'date-fns';

import { ClaimType, DamageDescription, MissingItemDescription, ClaimContext } from './types';
import {
  ClaimRequirements,
  ClaimTypeRequirements,
  ConsumerActionStatus,
  OptionalRequirement,
} from '../../../api/types';
import { ClaimContextForAdditionalInfoStep } from './common/AdditionalInfoStep';

export const currencies = ['EUR', 'CHF', 'CZK', 'DKK', 'GBP', 'HUF', 'NOK', 'PLN', 'RON', 'SEK'];

const countryCurrencies: Record<string, string | undefined> = {
  li: 'CHF',
  cz: 'CZK',
  dk: 'DKK',
  gb: 'GBP',
  hu: 'HUF',
  no: 'NOK',
  pl: 'PLN',
  ro: 'RON',
  se: 'SEK',
};

export const languageLabels: Record<string, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  nl: 'Dutch',
  it: 'Italian',
  pl: 'Polish',
  pt: 'Portuguese',
};

export function createEmptyInfoItem(): MissingItemDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
  };
}

export function createEmptyDamageDescription(): DamageDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
    damageDescription: '',
  };
}

export function formatDate(date: Date | string): string {
  return format(date, 'dd.MM.yyyy');
}

export function getCurrencyOfCountry(countryCode?: string | null): string {
  return countryCurrencies[countryCode || ''] || 'EUR';
}

/**
 * Converts a fraction (e.g. 0.07) to a percentage (7) for display, avoiding
 * floating point artifacts like 0.07 * 100 === 7.000000000000001
 */
export function toPercentage(fraction: number): number {
  return Math.round(fraction * 100 * 100) / 100;
}

export function isWarehouseDeliveryDateRequired(claim: ClaimContext) {
  const { useWarehouseDelivery, outcome } = getDeadline(claim) || {};

  if (!useWarehouseDelivery) {
    return false;
  }

  return outcome === 'early' || outcome === 'past';
}

export function isNonManual(claim: Pick<ClaimContext, 'shipmentId'>) {
  return !!claim.shipmentId;
}

function getDeadline(claim: ClaimContext) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].deadline : null;
}

export function getClaimableWarning(claim: Pick<ClaimContext, 'submissionInfo' | 'type'>) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].warning : null;
}

export function isClaimWithMissingTrackingData(
  claim: Pick<ClaimContext, 'submissionInfo' | 'type' | 'shipmentId' | 'entryPoint'>
) {
  return getClaimableWarning(claim) === 'no_required_tracking_data';
}

export function isManualDraft(claim: Pick<ClaimContext, 'shipmentId' | 'draftId'>) {
  return claim.draftId && !claim.shipmentId;
}

export function isClaimOutsideOfDeadline(claim: ClaimContext) {
  const { outcome } = getDeadline(claim) || {};
  // it's better to not compare against "within" because for manual flow we don't have deadlines
  return outcome === 'early' || outcome === 'past';
}

export function isClaimWithMailboxDeliveryWarning(claim: ClaimContext) {
  return getClaimableWarning(claim) === 'mailbox_delivery_no_liability';
}

export function isShipmentDelivered(claim: ClaimContext) {
  return claim.isDelivered;
}

export function isInvestigationClaim(claim: ClaimContext) {
  return claim.type === 'investigation';
}

const damageOrMissingItemClaimTypes = [ClaimType.MISSING_ITEM, ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE];

export function isDhlDe(context: Pick<ClaimContext, 'carrierCode' | 'carrierCountryCode'>) {
  return context.carrierCode === 'dhl' && context.carrierCountryCode === 'de';
}

/**
 * Special submission flow (Shop Apotheke) where the claim can only be saved as a draft at the end
 * and some validation rules change during submission. Applies to investigation claims unconditionally,
 * and to damage/missing-item claims only for non-DHL DE carriers.
 */
export function isSpecialFlow(
  context: Pick<ClaimContext, 'shopSettings' | 'type' | 'carrierCode' | 'carrierCountryCode'>
) {
  if (!context.type || !context.shopSettings?.isSpecialFlowEnabled) {
    return false;
  }

  if (context.type === ClaimType.INVESTIGATION) {
    return true;
  }

  return damageOrMissingItemClaimTypes.includes(context.type) && !isDhlDe(context);
}

export type ClaimContextForSpecialFlowCompleteness = ClaimContextForAdditionalInfoStep &
  Pick<ClaimContext, 'invoiceDocuments' | 'netValue' | 'orderItems' | 'isOrderItemsRequired'>;

/**
 * Whether fields that are optional in special flow are all filled
 * If they are, we want to display submit claim button instead of save draft
 */
export function isSpecialFlowClaimComplete(context: ClaimContextForSpecialFlowCompleteness) {
  const { declarationOfRecipientRequirement, dataProcessingConsentRequirement } =
    computeAdditionalInfoRequirements(context);

  const hasInvoice = context.invoiceDocuments.length > 0;
  const hasNetValue = !!context.netValue;
  const hasOrderItems = !context.isOrderItemsRequired || context.orderItems.length > 0;
  const hasDOR = !declarationOfRecipientRequirement.isRequired || context.declarationOfRecipient.length > 0;
  const hasDataProcessingConsent =
    !dataProcessingConsentRequirement.isRequired || context.dataProcessingConsent.length > 0;

  return hasInvoice && hasNetValue && hasOrderItems && hasDOR && hasDataProcessingConsent;
}

export function isPartialDamageClaim(claim: ClaimContext) {
  return claim.type === 'partial_damage';
}

export function isWholeDamageClaim(claim: ClaimContext) {
  return claim.type === 'whole_damage';
}

export function isMissingItemClaim(claim: ClaimContext) {
  return claim.type === 'missing_item';
}

export function isCashOnDeliveryClaim(claim: ClaimContext) {
  return claim.type === 'cash_on_delivery';
}

export function isClaimsCenter(claim: Pick<ClaimContext, 'entryPoint'>) {
  return claim.entryPoint === 'claims-center';
}

export function isManualClaim(claim: Pick<ClaimContext, 'entryPoint' | 'shipmentId'>) {
  return isClaimsCenter(claim) && !isNonManual(claim);
}

export function isClaimsCenterAndNonManual(claim: ClaimContext) {
  return isClaimsCenter(claim) && isNonManual(claim);
}

export function isAdditionalInfoRequired(context: ClaimContext) {
  const { isAdditionalInfoRequired } = computeAdditionalInfoRequirements(context);
  return isAdditionalInfoRequired;
}

export function isInvoiceRequired(context: Pick<ClaimContext, 'type'>) {
  return context.type !== ClaimType.CASH_ON_DELIVERY;
}

export function isRequirementShown(requirement: OptionalRequirement | null | undefined): boolean {
  return requirement === 'required' || requirement === 'optional';
}

export function isRequirementRequired(requirement: OptionalRequirement | null | undefined): boolean {
  return requirement === 'required';
}

export interface ClaimFieldRequirement {
  isShown: boolean;
  isRequired: boolean;
}

export function toFieldRequirement(requirement: OptionalRequirement | null | undefined): ClaimFieldRequirement {
  return {
    isShown: isRequirementShown(requirement),
    isRequired: isRequirementRequired(requirement),
  };
}

export function getClaimTypeRequirements(
  claimRequirements: ClaimRequirements | null | undefined,
  type: ClaimType | undefined
): ClaimTypeRequirements | undefined {
  if (!claimRequirements || !type) {
    return undefined;
  }

  return claimRequirements[type];
}

export function computeAdditionalInfoRequirements(context: ClaimContextForAdditionalInfoStep) {
  const typeRequirements = getClaimTypeRequirements(context.claimRequirements, context.type);

  const isCashOnDeliveryClaim = context.type === ClaimType.CASH_ON_DELIVERY;

  const weightRequirement = toFieldRequirement(typeRequirements?.weight);
  const recipientPhoneNumberRequirement = toFieldRequirement(typeRequirements?.recipientPhoneNumber);
  const carrierCaseIdRequirement = toFieldRequirement(typeRequirements?.carrierCaseId);
  const carrierDamageReportRequirement = toFieldRequirement(typeRequirements?.carrierDamageReport);
  const declarationOfRecipientRequirement = toFieldRequirement(typeRequirements?.declarationOfRecipient);
  const picturesOfMissingItemRequirement = toFieldRequirement(typeRequirements?.picturesOfMissingItem);
  const damagePhotosRequirement = toFieldRequirement(typeRequirements?.picturesOfDamage);
  const damageDescriptionRequirement = toFieldRequirement(typeRequirements?.damageDescription);

  // the consent is only collected when no DOR covers the claim and it is either a
  // required upload or not rendered at all
  // reason we do not have claims-api give us real requirement is because we need to know
  // if consent is enabled for a shipment
  // to display e-DOR or Consent button in frontend, so we send required in all claim types as a workaround
  // TODO: send processing consent configuration apart from requirement to remove this workaround
  const isDataProcessingConsentRequired =
    isRequirementRequired(typeRequirements?.dataProcessingConsent) && !declarationOfRecipientRequirement.isShown;
  const dataProcessingConsentRequirement: ClaimFieldRequirement = {
    isShown: isDataProcessingConsentRequired,
    isRequired: isDataProcessingConsentRequired,
  };

  const isPictureUploadLinkStepRequired = isPictureUploadLinkStepEnabled(context);

  const isCarrierSpecificDataRequired =
    weightRequirement.isShown ||
    recipientPhoneNumberRequirement.isShown ||
    carrierCaseIdRequirement.isShown ||
    carrierDamageReportRequirement.isShown ||
    dataProcessingConsentRequirement.isShown;

  const isAdditionalInfoRequired =
    declarationOfRecipientRequirement.isShown ||
    damageDescriptionRequirement.isShown ||
    damagePhotosRequirement.isShown ||
    picturesOfMissingItemRequirement.isShown ||
    isPictureUploadLinkStepRequired ||
    isCarrierSpecificDataRequired ||
    isCashOnDeliveryClaim;

  return {
    weightRequirement,
    recipientPhoneNumberRequirement,
    carrierCaseIdRequirement,
    carrierDamageReportRequirement,
    declarationOfRecipientRequirement,
    picturesOfMissingItemRequirement,
    damagePhotosRequirement,
    damageDescriptionRequirement,
    dataProcessingConsentRequirement,
    isPictureUploadLinkStepRequired,
    isCarrierSpecificDataRequired,
    isAdditionalInfoRequired,
  };
}

export function isPictureUploadLinkStepEnabled(
  context: Pick<ClaimContext, 'shopSettings' | 'picturesOfDamage' | 'picturesOfMissingItem' | 'type' | 'shipmentId'>
) {
  return (
    !!context.type &&
    damageOrMissingItemClaimTypes.includes(context.type) &&
    context.shopSettings.isPhotoCollectionAppEnabled &&
    !!context.shipmentId
  );
}

export function isDorSigningLinkStepEnabled(context: ClaimContext) {
  const declarationOfRecipient = getClaimTypeRequirements(
    context.claimRequirements,
    context.type
  )?.declarationOfRecipient;

  return (
    (context.shouldForceShowEDORStep || context.consumerActionStatus !== ConsumerActionStatus.COMPLETED) &&
    isShipmentDelivered(context) &&
    context.shopSettings.isDORSigningLinkEnabled &&
    isNonManual(context) &&
    isRequirementShown(declarationOfRecipient)
  );
}
